import { hbs } from 'ember-cli-htmlbars';
const __COLOCATED_TEMPLATE__ = hbs("{{! @glint-nocheck: not typesafe yet }}\n{{#if (or this.manualFailure this.didReceivePlatformErrors)}}\n  <Planner::ContentPillars::Error />\n{{else}}\n  <Planner::ContentPillars::List::Header\n    @aiCreditCountForPlan={{this.aiCreditCountForPlan}}\n    @hasAtLeastOneGeneratedPillar={{this.hasAtLeastOneGeneratedPillar}}\n    @remainingAICredits={{this.remainingAICredits}}\n  />\n  <main class=\"o--page__body\" data-test-id=\"content-pillars-list\">\n    <div class=\"u--m__lr__lg\">\n      <Planner::Form::BrandDescription\n        @model={{this.group}}\n        @onError={{perform this.onBrandDescriptionError}}\n        @onSubmit={{perform this.onBrandDescriptionSubmit}}\n      />\n    </div>\n    <ul class=\"cID--pillarContainer\">\n      {{#each @contentPillars as |contentPillar index|}}\n        <Planner::ContentPillars::Column @contentPillar={{contentPillar}} @index={{index}} />\n      {{/each}}\n    </ul>\n  </main>\n{{/if}}", {"contents":"{{! @glint-nocheck: not typesafe yet }}\n{{#if (or this.manualFailure this.didReceivePlatformErrors)}}\n  <Planner::ContentPillars::Error />\n{{else}}\n  <Planner::ContentPillars::List::Header\n    @aiCreditCountForPlan={{this.aiCreditCountForPlan}}\n    @hasAtLeastOneGeneratedPillar={{this.hasAtLeastOneGeneratedPillar}}\n    @remainingAICredits={{this.remainingAICredits}}\n  />\n  <main class=\"o--page__body\" data-test-id=\"content-pillars-list\">\n    <div class=\"u--m__lr__lg\">\n      <Planner::Form::BrandDescription\n        @model={{this.group}}\n        @onError={{perform this.onBrandDescriptionError}}\n        @onSubmit={{perform this.onBrandDescriptionSubmit}}\n      />\n    </div>\n    <ul class=\"cID--pillarContainer\">\n      {{#each @contentPillars as |contentPillar index|}}\n        <Planner::ContentPillars::Column @contentPillar={{contentPillar}} @index={{index}} />\n      {{/each}}\n    </ul>\n  </main>\n{{/if}}","moduleName":"later/components/planner/content-pillars/list.hbs","parseOptions":{"srcName":"later/components/planner/content-pillars/list.hbs"}});
import { sort } from '@ember/object/computed';
import { cancel, later } from '@ember/runloop';
import { inject as service } from '@ember/service';
import Component from '@glimmer/component';
import { task } from 'ember-concurrency';
import { tracked } from 'tracked-built-ins';

import { SegmentEventTypes } from 'later/utils/constants/segment-events';

import type { EmberRunTimer } from '@ember/runloop/types';
import type { EmberChangeset } from 'ember-changeset';
import type IntlService from 'ember-intl/services/intl';
import type { RawContentIdea } from 'later/models/content-idea';
import type ContentPillarModel from 'later/models/content-pillar';
import type { RawContentPillar } from 'later/models/content-pillar';
import type GroupModel from 'later/models/group';
import type AiCreditsService from 'later/services/ai-credits';
import type AlertsService from 'later/services/alerts';
import type AuthService from 'later/services/auth';
import type ErrorsService from 'later/services/errors';
import type FayeService from 'later/services/faye';
import type SegmentService from 'later/services/segment';
import type SubscriptionsService from 'later/services/subscriptions';
import type { Maybe } from 'shared/types';

const UPDATED_CONTENT_PILLAR_FAYE_EVENT = 'contentPillarUpdate';
const UPDATED_CONTENT_IDEA_FAYE_EVENT = 'contentIdeaUpdate';

interface ListComponentArgs {
  contentPillars: ContentPillarModel[];
}

export default class ListComponent extends Component<ListComponentArgs> {
  @service declare aiCredits: AiCreditsService;
  @service declare auth: AuthService;
  @service declare alerts: AlertsService;
  @service declare errors: ErrorsService;
  @service declare intl: IntlService;
  @service declare faye: FayeService;
  @service declare segment: SegmentService;
  @service declare subscriptions: SubscriptionsService;

  declare model: ContentPillarModel[];

  contentPillarSorting = ['id:asc'];
  timers: EmberRunTimer[] = [];

  @tracked manualFailure = false;

  @sort('contentPillars', 'contentPillarSorting') sortedContentPillars: Maybe<ContentPillarModel[]>;

  constructor(owner: unknown, args: ListComponentArgs) {
    super(owner, args);
    this.#setupFayeListeners();
    this.#performManualReload(30000);
    this.#performManualReload(60000);
    this.#setManualFailure(61000);
    this.segment.track(SegmentEventTypes.ViewedPage, {
      page: 'content pillars'
    });
  }

  willDestroy(): void {
    super.willDestroy();
    this.timers.forEach((timer: EmberRunTimer) => cancel(timer));
  }

  get group(): GroupModel {
    return this.auth.currentGroup;
  }

  get aiCreditCountForPlan(): Maybe<number> {
    return this.subscriptions.subscriptionPlan?.numberOfAiCredits;
  }

  get didNotGenerateTitles(): boolean {
    return Boolean(
      this.args.contentPillars?.length &&
        this.args.contentPillars?.every((contentPillar: ContentPillarModel) => !contentPillar.generatedTitle)
    );
  }

  get didReceivePlatformErrors(): boolean {
    return Boolean(
      this.args.contentPillars?.length &&
        this.args.contentPillars?.every(
          (contentPillar: ContentPillarModel) => contentPillar.platformError && !contentPillar.title
        )
    );
  }

  get hasAtLeastOneGeneratedPillar(): boolean {
    return Boolean(this.args.contentPillars.some((contentPillar: ContentPillarModel) => contentPillar.title));
  }

  get remainingAICredits(): number | undefined {
    return this.aiCredits.remainingAICredits;
  }

  #performManualReload(timeout: number): void {
    const timer = later(
      this,
      () => {
        if (this.didNotGenerateTitles) {
          this.args.contentPillars.forEach((contentPillar: ContentPillarModel) => {
            contentPillar.reload();
          });
        }
      },
      timeout
    );
    this.timers.push(timer);
  }

  #setManualFailure(timeout: number): void {
    const timer = later(
      this,
      () => {
        if (this.didNotGenerateTitles) {
          this.#trackAiGenerationTimeouts();
          this.manualFailure = true;
        }
      },
      timeout
    );
    this.timers.push(timer);
  }

  #setupFayeListeners(): void {
    this.faye?.on(UPDATED_CONTENT_PILLAR_FAYE_EVENT, ({ ContentPillar }: { ContentPillar: RawContentPillar }) => {
      if (ContentPillar.platform_warning && !ContentPillar.platform_error && !ContentPillar.title) {
        this.alerts.info(this.intl.t('alerts.content_pillars.pillars.still_generating'));
      }
    });

    this.faye?.on(UPDATED_CONTENT_IDEA_FAYE_EVENT, ({ ContentIdea }: { ContentIdea: RawContentIdea }) => {
      if (ContentIdea.platform_warning && !ContentIdea.platform_error && !ContentIdea.title) {
        this.alerts.info(this.intl.t('alerts.content_pillars.ideas.still_generating'));
      }
    });
  }

  #trackAiGenerationTimeouts(): void {
    this.args.contentPillars.forEach((contentPillar: ContentPillarModel) => {
      if (!contentPillar.platformError) {
        this.segment.track(SegmentEventTypes.AiGenerationTimeout, {
          model: 'content pillar',
          id: contentPillar.id
        });
      }
    });
  }

  onBrandDescriptionError = task(async (error: Error | string, changeset: EmberChangeset) => {
    changeset.rollback();
    this.errors.log(`An error occurred while attempting to update brand description: ${error}`);
    this.alerts.warning(this.intl.t('content_pillars.brand_description_form.error_message'), {
      title: this.intl.t('content_pillars.brand_description_form.error_message')
    });
  });

  onBrandDescriptionSubmit = task({ drop: true }, async (changeset: EmberChangeset) => {
    if (changeset.isDirty) {
      changeset.useShortBioAuth = true;
      await changeset.save();
    }
  });
}
